"use client";

import React, { useState, useRef, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";

const defaultFaqItems = [
  {
    question: "What services does Evinov offer?",
    answer:
      "Evinov offers full web development services including custom web applications, technical consulting, software testing and QA, SEO optimization, UI/UX design, and ongoing website maintenance.",
  },
  {
    question: "Do you build custom web applications?",
    answer:
      "Yes, we design and develop custom web applications tailored to your business needs, from simple websites to complex, scalable platforms.",
  },
  {
    question: "What does your consulting service include?",
    answer:
      "Our consulting service covers technical audits, architecture planning, technology stack recommendations, and strategic guidance to help you make informed decisions for your digital projects.",
  },
  {
    question: "Do you offer testing and quality assurance?",
    answer:
      "Absolutely. We provide thorough testing services including functional testing, performance testing, and bug tracking to ensure your web application runs smoothly before and after launch.",
  },
  {
    question: "Can you improve my website's SEO?",
    answer:
      "Yes, we offer SEO services including on-page optimization, technical SEO audits, site speed improvements, and content strategy to help your website rank better and attract more traffic.",
  },
  {
    question: "Do you provide UI/UX design services?",
    answer:
      "Yes, our team designs intuitive and visually appealing interfaces, focusing on user experience research, wireframing, prototyping, and modern design principles.",
  },
  {
    question: "What does your maintenance service cover?",
    answer:
      "Our maintenance service includes regular updates, security monitoring, bug fixes, performance optimization, and technical support to keep your web application running reliably.",
  },
  {
    question: "Do you offer subscription plans?",
    answer:
      "Yes, we offer yearly subscription plans that bundle maintenance, support, and updates at a fixed annual fee, giving you predictable costs and continuous peace of mind.",
  },
  {
    question: "How much does a web development project cost?",
    answer:
      "Costs vary depending on project scope and complexity. Contact us for a free consultation and personalized quote based on your specific requirements.",
  },
  {
    question: "How long does it take to build a web application?",
    answer:
      "Timelines depend on the project's scope, but most web applications take 4–12 weeks from planning to launch. We'll provide a detailed timeline during our initial consultation.",
  },
];

const FAQContainer = ({
  title = "Frequently\nAsked Questions",
  description = "Find quick answers to common questions about our services, process, technology, pricing, and ongoing support.",
  faq = defaultFaqItems,
  fullWidth = false,
}) => {
  const [activeIndices, setActiveIndices] = useState([]);
  const iconRefs = useRef([]);
  const contentRefs = useRef([]);
  const faqItemsRef = useRef([]);

  useEffect(() => {
    iconRefs.current = iconRefs.current.slice(0, faq.length);
    contentRefs.current = contentRefs.current.slice(0, faq.length);
    faqItemsRef.current = faqItemsRef.current.slice(0, faq.length);

    gsap.fromTo(
      faqItemsRef.current,
      {
        opacity: 0,
        y: 20,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        stagger: 0.1,
        delay: 1.25,
      }
    );
  }, [faq]);

  const toggleFAQ = (index) => {
    if (activeIndices.includes(index)) {
      gsap.to(iconRefs.current[index], {
        rotation: 0,
        duration: 0.3,
        ease: "power2.out",
      });

      gsap.to(contentRefs.current[index], {
        height: 0,
        opacity: 0,
        duration: 0.4,
        ease: "power2.out",
        paddingTop: 0,
        paddingBottom: 0,
      });

      setActiveIndices(activeIndices.filter((i) => i !== index));
    } else {
      gsap.to(iconRefs.current[index], {
        rotation: 90,
        duration: 0.3,
        ease: "power2.out",
      });

      const contentHeight = contentRefs.current[index].scrollHeight;

      gsap.to(contentRefs.current[index], {
        height: contentHeight + 24,
        opacity: 1,
        duration: 0.5,
        ease: "power2.out",
        paddingTop: "0.5em",
        paddingBottom: "0.5em",
      });

      setActiveIndices([...activeIndices, index]);
    }
  };

  return (
    // .faq-container { width:100%; padding:2em; } + media query padding:1.25em
    <div className="w-full p-5 lg:p-8 bg-background">
      <div
        // .faq-wrapper.full-width -> w-full py-8
        // .faq-wrapper.contained -> mx-auto w-3/5 py-8, collapsing to full width below lg
        className={
          fullWidth
            ? "w-full py-8"
            : "lg:mx-16 py-8"
        }
      >
        <div className="flex justify-between gap-2 flex-wrap items-center">
          <h2 className="text-4xl lg:text-7xl font-bold whitespace-pre-line">
            {title}
          </h2>
          <p className="lg:text-xl lg:w-1/3">
            {description}
          </p>
        </div>

        {/* .faq-items { margin-top:4em } */}
        <div className="faq-items grid grid-cols-1 md:grid-cols-2 gap-5 mt-16">
          {faq.map((item, index) => (
            <div
              key={index}
              // .faq-item
              className="mb-4 px-4 py-1 border border-dashed border-secondary rounded-2xl"
              ref={(el) => (faqItemsRef.current[index] = el)}
            >
              <div
                // .faq-question
                className="py-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleFAQ(index)}
              >
                {/* .faq-question h3 */}
                <h3 className="m-0 text-lg lg:text-xl tracking-normal leading-6 select-none">
                  {item.question}
                </h3>
                <span
                  // .faq-icon
                  className="flex items-center justify-center"
                  ref={(el) => (iconRefs.current[index] = el)}
                >
                  <ArrowRight size={20} />
                </span>
              </div>
              <div
                // .faq-answer
                className="relative will-change-transform"
                ref={(el) => (contentRefs.current[index] = el)}
                style={{ height: 0, opacity: 0, overflow: "hidden" }}
              >
                {/* .faq-answer p */}
                <p className="relative m-0 w-full select-none break-words whitespace-normal will-change-[transform,opacity]">
                  {item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQContainer;